import { useMemo } from "react";

import { customRequest } from "app/modules/api-client";
import { useLLMReportQuery } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/hooks/useLLMReportQuery";

import type { ILLMWidgetRequestParams } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";

import { INSIGHT_TYPE_CONFIG } from "./consts";
import type { ICompetitiveInsightsResponse, IInsightEntry, InsightType } from "./types";
import { INSIGHT_TYPE } from "./types";

const fetchCompetitiveInsights = async (
    params: ILLMWidgetRequestParams,
): Promise<ICompetitiveInsightsResponse> =>
    customRequest<ICompetitiveInsightsResponse>({
        path: "/api/aiTracker/reports/insights/competitive",
        method: "GET",
        query: params,
        format: "json",
    });

const KNOWN_INSIGHT_TYPES = new Set<string>(Object.values(INSIGHT_TYPE));

export const transformInsights = (
    data: ICompetitiveInsightsResponse | undefined,
): IInsightEntry[] => {
    if (!data?.insights) return [];

    return Object.entries(data.insights)
        .filter(
            (entry): entry is [string, string] =>
                KNOWN_INSIGHT_TYPES.has(entry[0]) &&
                !!entry[1] &&
                !!INSIGHT_TYPE_CONFIG[entry[0] as InsightType],
        )
        .map(([key, text]) => ({ type: key as InsightType, text }))
        .slice(0, 3);
};

export const useCompetitiveInsights = (
    params: ILLMWidgetRequestParams,
    options?: { enabled?: boolean },
) => {
    const { data, isLoading, isError } = useLLMReportQuery<
        ICompetitiveInsightsResponse,
        ILLMWidgetRequestParams
    >("competitiveInsights", fetchCompetitiveInsights, params, options);

    const insights = useMemo<IInsightEntry[]>(() => transformInsights(data), [data]);

    return { insights, isLoading, isError };
};
